// frontend/src/pages/settings/SessionsDevicesPage.tsx
//
// Mockup screen 2. Not a list of sessions: a PAIR OF SLOTS each, full or empty,
// so the rule is visible at a glance. An empty slot is where a second device
// would have to go — which is exactly what is refused.
//
// 🔒 A FAILED LOAD MUST NEVER DRAW THE BOARD. An empty roster under
// "0 phones · 0 browsers · 0 slots of a possible 0" is not "we could not find
// out" — it is an affirmative NOBODY IS SIGNED IN, printed on the one screen an
// admin consults to decide whether an unrecognised device is holding somebody's
// slot. Read the wrong way once and this feature has actively misled the person
// it exists to inform. On failure the counts, the search and the list are all
// replaced by a failure notice and a retry.
//
// 🔒 SIGN OUT ONE, OR BOTH (spec §4). "Sign out both" alone is not the screen
// the spec describes, and the difference is a real person: an admin who sees an
// unrecognised browser holding somebody's slot could otherwise only sign out
// BOTH slots — killing that person's phone too, in the middle of their day, for
// no reason. Each FULL slot therefore carries its own control; an EMPTY slot has
// nothing to end and offers no button.
//
// 🔒 PLATFORM ONLY (P3). No product vocabulary — copied to Sun Stellar unchanged.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, Smartphone, Monitor } from 'lucide-react';
import { api } from '../../utils/api';
import { agoShort } from '../../utils/agoWords';

interface Slot {
  channel: 'web' | 'apk';
  sessionId: string | null;
  label: string | null;
  clientVersion: string | null;
  networkName: string | null;
  lastSeenAt: string | null;
}
interface Person { id: string; name: string; email: string; roleName: string; slots: Slot[] }

export function SessionsDevicesPage() {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busySessionId, setBusySessionId] = useState<string | null>(null);
  // 🔒 A SIGN-OUT THAT FAILED IS NOT A LOAD THAT FAILED. Reusing the load
  // failure would replace a board we DID read with "nothing below this line is
  // known right now" — throwing away good information and telling the admin the
  // opposite of what happened. This notice sits above a board that is still true.
  const [signOutFailed, setSignOutFailed] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/sessions/admin/people');
      setPeople(res.data.data ?? []);
      setFailed(false);
    } catch {
      // 🔒 Clear the roster on the way out — a stale board beside a failure
      // notice is two answers to the same question.
      setPeople([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return people;
    return people.filter(p =>
      [p.name, p.email, p.roleName, ...p.slots.map(s => `${s.label ?? ''} ${s.networkName ?? ''}`)]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [people, q]);

  const counts = useMemo(() => {
    let phones = 0, browsers = 0;
    for (const p of people) for (const s of p.slots) {
      if (!s.sessionId) continue;
      if (s.channel === 'apk') phones += 1; else browsers += 1;
    }
    return { phones, browsers, held: phones + browsers, total: people.length * 2 };
  }, [people]);

  /** End ONE slot — spec §4, "Sign out one, or both". */
  async function signOutSlot(sessionId: string) {
    setBusySessionId(sessionId);
    setSignOutFailed(false);
    try {
      await api.delete(`/sessions/admin/sessions/${sessionId}`);
      await load();
    } catch {
      setSignOutFailed(true);
    } finally {
      setBusySessionId(null);
    }
  }

  async function signOutBoth(userId: string) {
    setBusyId(userId);
    try {
      await api.post(`/sessions/admin/people/${userId}/sign-out-both`);
      await load();
    } catch {
      setFailed(true);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-slate-900">Who is signed in</h1>

      {/* 🔒 The counts and the board are drawn ONLY on a load that succeeded.
          A zero line over an empty roster is a claim, not a blank. */}
      {!loading && !failed && (
        <>
          <p className="mt-1 text-sm text-slate-600">
            {counts.phones} phone{counts.phones === 1 ? '' : 's'} · {counts.browsers} browser
            {counts.browsers === 1 ? '' : 's'} · {counts.held} slot{counts.held === 1 ? '' : 's'} of a
            possible {counts.total}
          </p>

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search person, device, network…"
              className="w-full text-sm outline-none"
            />
          </div>
        </>
      )}

      {/* 🔒 Distinct from the load failure below, deliberately: this one says
          the board is still good and the slot is probably still held. */}
      {!loading && !failed && signOutFailed && (
        <div className="mt-4 rounded-lg border p-3" style={{ borderColor: '#efc15d', background: '#fff8df' }}>
          <p className="text-sm font-medium" style={{ color: '#8a6d1f' }}>
            Could not sign that device out — it may still be holding the slot.
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Everything below is what we last read successfully. Try the button again.
          </p>
        </div>
      )}

      {loading ? (
        <Loader2 className="mt-6 h-5 w-5 animate-spin text-slate-400" />
      ) : failed ? (
        <div className="mt-4 rounded-lg border p-4" style={{ borderColor: '#d77b76', background: '#fff5f4' }}>
          <p className="text-sm font-medium" style={{ color: '#9b3936' }}>
            Could not load who is signed in — try again.
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Nothing below this line is known right now. This is not a report that nobody is signed in.
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="mt-4 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
          {filtered.map(p => (
            <div key={p.id} className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center">
              <div className="w-full lg:w-64">
                <Link to={`/settings/sessions/${p.id}`} className="text-sm font-medium text-slate-900 hover:underline">
                  {p.name}
                </Link>
                <p className="text-xs text-slate-500">{p.email}</p>
                <p className="text-xs text-slate-400">{p.roleName}</p>
              </div>
              <div className="grid flex-1 gap-2 sm:grid-cols-2">
                {p.slots.map(s => {
                  const Icon = s.channel === 'apk' ? Smartphone : Monitor;
                  return (
                    <div key={s.channel} className="rounded-lg border border-slate-200 p-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        {s.channel === 'apk' ? 'Phone' : 'Browser'}
                      </p>
                      {s.sessionId ? (
                        <>
                          <div className="mt-1 flex items-start gap-2">
                            <Icon className="mt-0.5 h-4 w-4 text-slate-500" />
                            <div className="min-w-0">
                              <p className="truncate text-sm text-slate-900">{s.label}</p>
                              <p className="truncate text-xs text-slate-500">
                                {[s.clientVersion, s.networkName, agoShort(s.lastSeenAt)].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                          </div>
                          {/* 🔒 THIS slot, not the pair. Same wording as the card a
                              person sees for their own devices, so the two screens
                              read as one feature. */}
                          <button
                            type="button"
                            onClick={() => void signOutSlot(s.sessionId as string)}
                            disabled={busySessionId === s.sessionId}
                            className="mt-2 inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-[11px] font-medium disabled:opacity-40"
                            style={{ borderColor: '#d77b76', color: 'var(--danger, #c34a48)' }}
                          >
                            {busySessionId === s.sessionId && <Loader2 className="h-3 w-3 animate-spin" />}
                            Sign out the {s.channel === 'apk' ? 'phone' : 'browser'}
                          </button>
                        </>
                      ) : (
                        <p className="mt-1 text-sm text-slate-400">Not signed in</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => void signOutBoth(p.id)}
                disabled={busyId === p.id || p.slots.every(s => !s.sessionId)}
                className="inline-flex items-center gap-2 self-start rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-40"
                style={{ borderColor: '#d77b76', color: 'var(--danger, #c34a48)' }}
              >
                {busyId === p.id && <Loader2 className="h-3 w-3 animate-spin" />}
                Sign out both
              </button>
            </div>
          ))}
        </div>
      )}

      {!loading && !failed && (
      <p className="mt-4 text-xs text-slate-500">
        Two slots each, always drawn, full or empty. An empty slot is as much information as a full
        one, and it is where a second device would have to go — which is exactly what is refused.
      </p>
      )}
    </div>
  );
}
